import { QueryFactory } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import unauthorizedWrapperStyles from '@lmt/unicorn-shared/app/components/login-form/UnauthorizedWrapper.module.scss';
import { WindowControls } from '@lmt/unicorn-shared/app/components/toolbar/UniToolBar';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useErrorHandler } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { UniStorageFactory } from '@lmt/unicorn-shared/app/helpers/uni-storage';

import styles from '@lmt/unicorn-shared/src/app/components/reset-password-page/ResetPasswordPage.module.scss';

type FormData = {
  username: string;
  newPassword: string;
  confirmPassword: string;
};

export const ResetPasswordPage: React.FC = (props) => {
  const i18next = useScopedTranslation('change_password_page');
  const {
    queryParams: { username, passwordResetToken },
  } = pages.passwordReset.useParams();
  const { handleSubmit, register, getValues, formState, setError, reset } =
    useForm<FormData>({
      shouldFocusError: true,
      criteriaMode: 'all',
      defaultValues: {
        username: username ?? '',
        newPassword: '',
        confirmPassword: '',
      },
    });

  const { handler: submitHandler, overallServerError } =
    useErrorHandler<FormData>(
      useCallback(
        async (data: FormData, navigate) => {
          await QueryFactory.UserClient.resetPassword({
            username: username || data.username,
            token: passwordResetToken,
            newPassword: data.newPassword,
          });

          UniStorageFactory.UniStorage.LastUserName = username || data.username;
          navigate(pages.home.link());
          toast(i18next.t('change_password_successful_toast'), {
            type: 'success',
          });
        },
        [passwordResetToken],
      ),
      setError,
    );
  return (
    <div className={unauthorizedWrapperStyles.mainContainer}>
      {isElectron() && !!WindowControls && (
        <div className={unauthorizedWrapperStyles.header}>
          <div className={styles.dragContainer}></div>
          <div className={styles.windowControls}>{WindowControls}</div>
        </div>
      )}
      <div className={unauthorizedWrapperStyles.mainSubcontainer}>
        <div className={styles.formContainer}>
          <div className={styles.title}>{i18next.t('title')}</div>
          <form
            className={styles.form}
            onSubmit={handleSubmit(submitHandler)}
            data-test-id="changeOwnPasswordPopup"
          >
            <div className={styles.fields}>
              {!username ? (
                <Field
                  className={styles.field}
                  title={i18next.t('username')}
                  titleClassName={styles.fieldTitle}
                >
                  <Input
                    {...register('username', requiredRule())}
                    errorText={formState.errors.username?.message}
                    errorClassname={styles['input-error-text']}
                  />
                </Field>
              ) : null}
              <Field
                className={styles.field}
                title={i18next.t('new_password')}
                titleClassName={styles.fieldTitle}
              >
                <Input
                  type="password"
                  {...register('newPassword', requiredRule())}
                  errorText={formState.errors.newPassword?.message}
                  errorClassname={styles['input-error-text']}
                />
              </Field>
              <Field
                className={styles.field}
                title={i18next.t('confirm_password')}
                titleClassName={styles.fieldTitle}
              >
                <Input
                  {...register('confirmPassword', {
                    ...requiredRule(),
                    validate: (value: string) =>
                      value === getValues('newPassword') ||
                      (i18next.t('passwords_dont_match') as string),
                  })}
                  type="password"
                  errorText={formState.errors.confirmPassword?.message}
                  errorClassname={styles['input-error-text']}
                />
              </Field>
              <div className={styles.submitContainer}>
                <div className={styles.error}>
                  {overallServerError.indexOf('token') > 0
                    ? overallServerError
                    : null}
                </div>
                <SubmitButton
                  submitting={formState.isSubmitting}
                  className={styles.submit}
                  title={i18next.t('change_own_password_button')}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
